// 手動で入れた Mod・プラグイン（Panel の構成に無い jar）の見え方と、jar の中の名前・版の読み方。
// 読むのは宣言ファイル（plugin.yml・fabric.mod.json・mods.toml）の決まった数項目だけ。ZIP の展開は呼び出し側が行う。
import { z } from 'zod';

export const ManualAddonKindSchema = z.enum(['mod', 'plugin']);
export type ManualAddonKind = z.infer<typeof ManualAddonKindSchema>;

/** jar の中に書かれた名前と版。id は同じ Mod・プラグインかどうかを見分けるのに使う */
export const AddonIdentitySchema = z.object({
  id: z.string().min(1).max(200),
  name: z.string().max(200).optional(),
  version: z.string().max(200).optional(),
});
export type AddonIdentity = z.infer<typeof AddonIdentitySchema>;

export const ManualAddonSchema = z.object({
  kind: ManualAddonKindSchema,
  /** ファイルの場所（ファイルタブと同じ形。例: plugins/LuckyCasino.jar） */
  path: z.string(),
  fileName: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  modifiedAt: z.string(),
  /** Panel（Modrinth・CurseForge）が入れた jar なら true。一覧には出さず、入れ替えの重なりの確認に使う */
  managed: z.boolean().default(false),
  identity: AddonIdentitySchema.optional(),
  /** jar を読めなかったときの理由 */
  error: z.string().optional(),
  /**
   * ランチャーでも配っているとき（Mod だけ）。stale はサーバーの jar と Panel の写しが違うらしい
   * （ファイル名か大きさが違う。SFTP などで Panel を通さずに置き換えたとき）
   */
  distribution: z
    .object({ side: z.enum(['required', 'optional']), stale: z.boolean(), version: z.string().optional() })
    .optional(),
});
export type ManualAddon = z.infer<typeof ManualAddonSchema>;

/** 手動で入れた Mod のうち、ランチャーでも配ると選んだ jar（Panel が持つ写し） */
export const ClientJarSchema = z.object({
  /** jar の中の Mod の id。入れ替えても同じ id なら写しも入れ替える */
  modId: z.string().min(1).max(200),
  name: z.string().max(200).optional(),
  version: z.string().max(200).optional(),
  fileName: z.string(),
  sha1: z.string().regex(/^[0-9a-f]{40}$/),
  sha512: z.string().regex(/^[0-9a-f]{128}$/),
  size: z.number().int().nonnegative(),
  side: z.enum(['required', 'optional']),
  updatedAt: z.string(),
});
export type ClientJar = z.infer<typeof ClientJarSchema>;

export const ClientJarDistributionInputSchema = z
  .object({
    /** 手動で入れた Mod の jar のファイル名（custom-mods の中） */
    fileName: z.string().min(1).max(120),
    /** null なら配るのをやめる */
    side: z.enum(['required', 'optional']).nullable(),
  })
  .strict();
export type ClientJarDistributionInput = z.infer<typeof ClientJarDistributionInputSchema>;

export const ManualAddonScanSchema = z.object({ addons: z.array(ManualAddonSchema) });
export type ManualAddonScan = z.infer<typeof ManualAddonScanSchema>;

/** 種類ごとの置き場所（ファイルタブの名前） */
export function manualAddonRoot(kind: ManualAddonKind): 'plugins' | 'custom-mods' {
  return kind === 'plugin' ? 'plugins' : 'custom-mods';
}

export const ManualJarNameSchema = z
  .string()
  .max(120)
  .regex(/^[\p{L}\p{N} ._()+\-[\]]+\.jar$/u, 'ファイル名は .jar で終わり、使える文字だけにしてください');

const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim().slice(0, 200) : undefined);
/** YAML の一番上の階層の「key: 値」だけを読む（plugin.yml の name・version に足りる） */
function topLevel(source: string, key: string): string | undefined {
  const match = new RegExp(`^${key}:[ \\t]*(.*)$`, 'm').exec(source);
  if (!match) return undefined;
  const value = match[1]!.trim();
  const quoted = /^(['"])(.*?)\1/.exec(value);
  return text(quoted ? quoted[2]! : value.replace(/\s+#.*$/, ''));
}
/** TOML の最初の [[mods]] の中の「key = "値"」を読む。Forge の MDK の雛形は `[[mods]] #mandatory` と後ろに注釈が付く */
function tomlMods(source: string, key: string): string | undefined {
  const block = /^\[\[mods\]\][ \t]*(?:#.*)?$([\s\S]*?)(?=^[ \t]*\[|$(?![\s\S]))/m.exec(source)?.[1];
  if (!block) return undefined;
  const match = new RegExp(`^[ \\t]*${key}[ \\t]*=[ \\t]*(["'])(.*?)\\1`, 'm').exec(block);
  return text(match?.[2]);
}

/**
 * jar の中の名前・版を読む。read は jar の中の 1 項目（無ければ undefined）を返す。
 * プラグインは plugin.yml / paper-plugin.yml、Mod は fabric.mod.json / META-INF/neoforge.mods.toml / META-INF/mods.toml。
 * どれも無ければ undefined（その種類の jar ではない）。
 */
export function addonIdentity(
  kind: ManualAddonKind,
  read: (name: string) => Buffer | undefined,
): AddonIdentity | undefined {
  if (kind === 'plugin') {
    for (const descriptor of ['paper-plugin.yml', 'plugin.yml']) {
      const bytes = read(descriptor);
      if (!bytes) continue;
      const source = bytes.toString('utf8').replace(/^﻿/, '');
      const name = topLevel(source, 'name');
      if (name) return { id: name.toLowerCase(), name, version: topLevel(source, 'version') };
    }
    return undefined;
  }
  const fabric = read('fabric.mod.json');
  if (fabric) {
    const value = z
      .object({ id: z.string(), name: z.unknown().optional(), version: z.unknown().optional() })
      .safeParse(JSON.parse(fabric.toString('utf8').replace(/^﻿/, '')));
    if (value.success && text(value.data.id))
      return { id: text(value.data.id)!.toLowerCase(), name: text(value.data.name), version: text(value.data.version) };
  }
  for (const descriptor of ['META-INF/neoforge.mods.toml', 'META-INF/mods.toml']) {
    const bytes = read(descriptor);
    if (!bytes) continue;
    const source = bytes.toString('utf8');
    const id = tomlMods(source, 'modId');
    if (id) {
      const version = tomlMods(source, 'version');
      return {
        id: id.toLowerCase(),
        name: tomlMods(source, 'displayName'),
        version: version === '${file.jarVersion}' ? undefined : version,
      };
    }
  }
  return undefined;
}
